/**
 * CampaignBanner
 */
import { OmniElement, OmniStyleElement, html, css, nothing } from 'omni-ui';
import { formatDate, capitalizeWord } from '../helpers/utils.js';
import { CampaignStepStatus } from './campaign-step-status.js';
import CampaignsAPI from '../services/campaigns-api.js';

OmniStyleElement.register();

/**
 * @summary Class for CampaignBanner
 * @description Banner that stretch across campaign's main page summerizing and displaying the campaign's details. Includes an omni-tooltip that dispatches an event to open the campaign-edit modal.
 */
export class CampaignBanner extends OmniElement {
  /**
   * @property {Object} campaignSummary - object with all of the campaigns details
   * @property {Boolean} readonly - flag which indicates whether to render as readonly
   */
  static get properties() {
    return {
      campaignSummary: { type: Object },
      campaignCompletion: { type: Number },
      progressBarWidth: { type: Number },
      readonly: { type: Boolean },
      geoName: { type: String },
      categoryName: { type: String },
    };
  }

  /**
   * CampaignBanner Styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .omni h1 {
          margin-bottom: 0 !important;
        }

        .omni .campaign-banner-wrapper {
          display: grid;
          grid-template-columns: 14rem auto 20%;
          grid-template-rows: 1fr;
          grid-template-areas: 'image content accent';
          width: 100%;
          min-height: 7.36rem;
          border-radius: 0.71rem;
          background-color: var(
            --omni-campaign-banner-background,
            rgb(var(--rgb-core-light))
          );
          box-shadow: 0 0.714rem 4.29rem -0.36rem rgba(var(--rgb-nav-one), 0.08);
          margin-top: 3rem;
          position: relative;
        }

        .campaign-image-container {
          grid-area: image;
          width: 17.29rem;
          height: 120%;
          position: relative;
          left: 0.714rem;
          bottom: 0.714rem;
          background: linear-gradient(
            164deg,
            rgb(var(--rgb-primary)) 11%,
            rgb(var(--rgb-secondary)) 106%
          );
          box-shadow:
            0 1.07rem 0.857rem -0.64rem rgba(var(--rgb-nav-one), 0.15),
            0 1px 0.714rem -0.21rem rgba(var(--rgb-nav-one), 0.15);
          border-radius: 0.57rem;
        }

        /**
         * For aspect-ratio based on omni-card dimensions (240x185),
         * also matched in accordion view (48:37), taking into account the set
         * height of the campaign banner container in wider desktop mode.
         *
         * TODO: fix responsive variations
         */
        @media (min-width: 1216px) {
          .campaign-image-container {
            width: 12.11rem;
          }
        }

        .campaign-image {
          width: 100%;
          height: 100%;
          position: relative;
          right: 0.714rem;
          bottom: 0.714rem;
          border-radius: 0.57rem;
          background-repeat: no-repeat;
          background-position: center;
          background-size: cover;
          box-shadow:
            rgb(var(--rgb-nav-one) / 15%) 0 1px 10px -3px,
            rgb(var(--rgb-nav-one) / 18%) 0 15px 12px -9px; /* Shadow style copied from .omni .card */
        }

        .omni .campaign-info-container {
          grid-area: content;
          display: inline-flex;
          justify-content: space-around;
          flex-flow: row nowrap;
        }

        .omni .campaign-name {
          display: flex;
          flex-flow: column wrap;
          padding: 1.43rem 2rem 1.43rem 0;
          align-items: flex-start;
          min-width: 22%;
        }
        .omni .campaign-name > p,
        .omni .campaign-objective > h2 {
          word-break: break-all;
        }

        .omni .campaign-objective,
        .campaign-budget {
          display: flex;
          flex-flow: column wrap;
          align-items: flex-start;
          padding: 1.43rem 2rem 1.43rem 0;
        }

        .campaign-budget {
          min-width: 15%;
        }

        .omni .campaign-banner-accent {
          width: 100%;
          height: 100%;
          grid-area: accent;
          border-radius: 0 0.57rem 0.57rem 0;
          background-image: linear-gradient(
            109deg,
            rgb(var(--rgb-primary)) 11%,
            rgb(var(--rgb-secondary)) 106%
          );
          clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 40% 100%);
        }

        .omni .campaign-edit-container {
          grid-area: accent;
          display: flex;
          justify-content: end;
          align-items: flex-end;
          padding: 1.43rem;
          z-index: 3;
        }

        .campaign-completion-container {
          grid-area: accent;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: start;
          padding: 1.43rem 0 0 2.86rem;
          z-index: 2;
          color: rgb(var(--rgb-core-light));
        }

        .completion-bar-outer {
          display: flex;
          align-items: center;
          width: 5.714rem;
          height: 0.857rem;
          margin: 0 0.571rem 0 0;
          border-radius: 0.286rem;
          background-color: rgb(var(--rgb-core-light));
        }

        .completion-bar-inner {
          width: 5.714rem;
          height: 0.714rem;
          margin: 0 0.071rem 0 0.071rem;
          opacity: 0.75;
          border-radius: 0.286rem;
          background-image: linear-gradient(
            60deg,
            rgb(var(--rgb-primary)) 25%,
            rgb(var(--rgb-secondary)) 25%,
            rgb(var(--rgb-secondary)) 50%,
            rgb(var(--rgb-primary)) 50%,
            rgb(var(--rgb-primary)) 75%,
            rgb(var(--rgb-secondary)) 75%,
            rgb(var(--rgb-secondary)) 100%
          );
          background-size: 0.714rem 1.237rem;
        }

        .completion-bar {
          display: flex;
          align-items: center;
        }

        .progress-bar-number {
          min-width: 2.5rem;
          text-align: right;
        }

        @media (max-width: 1215px) {
          .omni .campaign-banner-wrapper {
            grid-template-columns: 20rem auto;
            grid-template-rows: 1fr max-content max-content 50px;
            grid-template-areas:
              'image content'
              '.    content'
              '.    progress'
              'accent accent';
          }

          .omni .campaign-info-container {
            display: flex;
            flex-flow: column nowrap;
          }

          .omni .campaign-objective,
          .campaign-budget {
            padding: 0 3.57rem 1.43rem 0;
          }

          .omni .campaign-banner-accent {
            border-radius: 0 0 0.57rem 0.57rem;
            clip-path: none;
          }

          .omni .campaign-edit-container {
            padding: 0;
            margin: auto 1.43rem auto 0;
          }

          .campaign-completion-container {
            grid-area: progress;
            color: rgb(var(--rgb-core-dark));
            padding: 0 0 1.43rem 0;
            align-items: start;
            justify-content: flex-start;
          }

          .completion-bar-outer {
            outline: 1px solid rgb(var(--rgb-primary));
          }

          .omni .campaign-image-container {
            height: 100%;
          }
        }

        @media (max-width: 768px) {
          .omni .campaign-banner-wrapper {
            grid-template-columns: 1fr;
            grid-template-rows: 1fr 1fr max-content 50px;
            grid-template-areas:
              'image'
              'content'
              'progress'
              'accent';
          }

          .omni .campaign-name {
            padding: 1.43rem 0 0 1.43rem;
          }

          .omni .campaign-objective,
          .campaign-budget {
            padding: 1.43rem 0 0 1.43rem;
          }

          .campaign-completion-container {
            padding: 1.43rem 0 1.43rem 1.43rem;
          }
        }
      `,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.campaignCompletion = CampaignStepStatus.percentComplete(
      this.campaignSummary,
    );
    this.progressBarWidth = 0;
    this.campaignsAPI = new CampaignsAPI(this.token, this.env);
  }

  firstUpdated() {
    this.animateProgress();
  }

  animateProgress() {
    const that = this;
    // eslint-disable-next-line no-use-before-define
    const id = setInterval(frame, 10);
    function frame() {
      if (that.progressBarWidth >= that.campaignCompletion) {
        clearInterval(id);
        return;
      }
      that.progressBarWidth += 1;
    }
  }

  /**
   * Fetches category/subcategory/market/region names for the campaign
   * via Omni-Dictionary
   *
   */
  fetchNames = async () => {
    const { category, subcategory, region, market } = this.campaignSummary;

    // subcategory call requires category information so optional param used
    this.categoryName = await (subcategory
      ? this.campaignsAPI.getDictionaryObjectName(
          'subcategory',
          category,
          subcategory,
        )
      : this.campaignsAPI.getDictionaryObjectName('category', category));

    // market is most granular, so if it exists, use it
    this.geoName = await this.campaignsAPI.getDictionaryObjectName(
      market ? 'market' : 'region',
      market ?? region,
    );
  };

  /**
   *
   * @returns main html template form CampaignBanner. Initializes variables from deconstructing {@link #campaignSummary}.
   */
  render() {
    const {
      name = '',
      start_date: startDate = '',
      end_date: endDate = '',
      description = '',
      image_url: imageUrl = '',
      budget,
      currency,
      so_type: soType,
      subcategory,
      market,
    } = this.campaignSummary;
    this.fetchNames();
    return html`
      <omni-style>
        <h1 class="title is-size-6 is-clamped has-text-grey-dark is-uppercase">
          Overview
        </h1>
        <section class="campaign-banner-wrapper">
          <div class="campaign-image-container">
            <div
              class="campaign-image"
              style="${imageUrl ? `background-image: url('${imageUrl}')` : ''}"
            ></div>
          </div>
          <div class="campaign-info-container">
            <div class="campaign-name">
              <h2
                class="title is-size-5 is-clamped has-text-grey-dark has-text-weight-normal mb-2"
              >
                ${capitalizeWord(soType)}
              </h2>
              <p class="is-clamped clamp-2 has-text-weight-bold">${name}</p>
              ${!!startDate?.trim() && !!endDate?.trim()
                ? html`
                    <p class="is-clamped clamp-2 has-text-grey-dark">
                      ${formatDate(startDate)} - ${formatDate(endDate)}
                    </p>
                  `
                : nothing}
            </div>
            ${budget
              ? html` <div class="campaign-budget">
                  <h2
                    class="title is-size-5 is-clamped has-text-grey-dark has-text-weight-normal mb-2"
                  >
                    Budget
                  </h2>
                  <h2 class="is-clamped clamp-2 has-text-weight-bold">
                    ${budget.toLocaleString('en-US', {
                      style: 'currency',
                      currency,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </h2>
                </div>`
              : ``}
            <div class="campaign-objective">
              <h2
                class="title is-size-5 is-clamped has-text-grey-dark has-text-weight-normal mb-2"
              >
                Objective
              </h2>
              <h2 class="is-clamped clamp-2 has-text-weight-bold">
                ${description}
              </h2>
            </div>
            <div class="campaign-budget">
              <h2 class="is-size-4 mb-2">${market ? 'Market' : 'Region'}</h2>
              <h2
                class="campaign-objective-text sidebar-text has-text-weight-bold"
              >
                ${this.geoName || 'n/a'}
              </h2>
            </div>
            <div class="campaign-budget">
              <h2 class="is-size-4 mb-2">
                ${subcategory ? 'Subcategory' : 'Category'}
              </h2>
              <h2
                class="campaign-objective-text sidebar-text has-text-weight-bold"
              >
                ${this.categoryName || 'n/a'}
              </h2>
            </div>
          </div>
          <div class="campaign-banner-accent"></div>
          ${this.readonly
            ? nothing
            : html`
                <div class="campaign-edit-container">
                  <omni-tooltip>
                    <omni-icon
                      style="--color-icon-lines:rgb(var(--rgb-core-light));"
                      slot="invoker"
                      class="is-size-2 is-clickable mr-4"
                      icon-id="omni:interactive:copy"
                      @click=${() => {
                        this.dispatchNewEvent('updateLocation', {
                          detail: { setParams: { modal: `${soType}-clone` } },
                          bubbles: true,
                          composed: true,
                        });
                      }}
                    ></omni-icon>
                    <div slot="content">Clone</div>
                  </omni-tooltip>
                  <omni-tooltip>
                    <omni-icon
                      style="--color-icon-lines:rgb(var(--rgb-core-light));"
                      slot="invoker"
                      class="is-size-2 is-clickable"
                      icon-id="omni:interactive:edit"
                      @click=${() => {
                        this.dispatchNewEvent('updateLocation', {
                          detail: { setParams: { modal: `${soType}-edit` } },
                          bubbles: true,
                          composed: true,
                        });
                      }}
                    ></omni-icon>
                    <div slot="content">Edit</div>
                  </omni-tooltip>
                </div>
              `}
          ${this.campaignCompletion
            ? html`<div class="campaign-completion-container">
                <h2 class="is-size-5 is-clamped has-text-weight-normal mb-2">
                  ${capitalizeWord(soType)} Progress
                </h2>
                <div class="completion-bar">
                  <div class="completion-bar-outer">
                    <div
                      class="completion-bar-inner"
                      style="width: ${this.progressBarWidth}%"
                    ></div>
                  </div>
                  <div class="progress-bar-number is-clamped clamped-2">
                    ${this.progressBarWidth}%
                  </div>
                </div>
              </div>`
            : null}
        </section>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-banner', CampaignBanner);
